import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment-timezone';
import Lokasi from './lokasi';
import Swal from 'sweetalert2';
import { 
  IoLocationOutline, 
  IoTimeOutline, 
  IoCheckmarkCircleOutline, 
  IoCloseCircleOutline,
  IoCamera,
  IoRefreshOutline,
  IoBusinessOutline
} from "react-icons/io5";
import { 
  Container, 
  Card, 
  Button, 
  LinearProgress,
  Avatar,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import { apiHost } from '../../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};

const ClockOut = () => {
  const navigate = useNavigate();
  
  const [imageSrc, setImageSrc] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoTaken, setPhotoTaken] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [namaCabang, setNamaCabang] = useState(null);
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const submittedRef = useRef(false);
  const animationRef = useRef(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const response = await axios.get(`${getApiBaseUrl()}/me`, { 
          withCredentials: true,
          timeout: 10000 
        });
        
        setProfileImage(response.data.url);
        
        if (response.data.cabang?.namacabang) {
          setNamaCabang(response.data.cabang.namacabang);
        } else {
          try {
            const branchResponse = await axios.get(`${getApiBaseUrl()}/cabang`, { 
              withCredentials: true,
              timeout: 5000 
            });
            setNamaCabang(branchResponse.data.namacabang || 'Kantor Pusat');
          } catch {
            setNamaCabang('Kantor Pusat');
          }
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
        setNamaCabang('Kantor Pusat');
      }
    };
  
    fetchProfileData();
  }, []);

  const startCamera = useCallback(async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user',
          frameRate: { ideal: 15, max: 24 }
        } 
      });
  
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setCameraActive(true);
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      Swal.fire({
        icon: 'error',
        title: 'Kesalahan',
        text: 'Tidak dapat mengakses kamera. Pastikan izin kamera diaktifkan.',
      });
    }
  }, []);

  useEffect(() => {
    if (!photoTaken) {
      startCamera();
    }
  
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [photoTaken, startCamera]);

  const getLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        Swal.fire({
          icon: 'error',
          title: 'Tidak Didukung',
          text: 'Geolokasi tidak didukung oleh browser Anda.',
        });
        reject(new Error('Geolocation not supported'));
        return;
      }

      const options = {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      };

      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
          resolve(position.coords);
        },
        (error) => {
          console.error('Error getting location:', error);
          Swal.fire({
            icon: 'error',
            title: 'Kesalahan',
            text: 'Gagal mendapatkan lokasi. Pastikan GPS aktif.',
          });
          reject(error);
        },
        options
      );
    });
  }, []);

  const takePicture = useCallback(async () => {
    try {
      if (!videoRef.current) {
        Swal.fire({ icon: 'warning', title: 'Peringatan', text: 'Kamera belum siap.' });
        return;
      }

      const photo = document.createElement('canvas');
      const video = videoRef.current;
      const targetWidth = 800;
      const targetHeight = (video.videoHeight / video.videoWidth) * targetWidth;
      photo.width = targetWidth;
      photo.height = targetHeight;
      const context = photo.getContext('2d');
      context.drawImage(video, 0, 0, targetWidth, targetHeight);
      const compressedImageUrl = photo.toDataURL('image/jpeg', 0.8);
      setImageSrc(compressedImageUrl);
      setPhotoTaken(true);

      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        setCameraActive(false);
      }

      try {
        await getLocation();
      } catch (err) {
        console.warn('Location fetch failed after capture:', err);
      }
    } catch (error) {
      console.error('Error taking picture:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Gagal mengambil foto'
      });
    }
  }, [getLocation]);

  const retakePicture = useCallback(() => {
    setPhotoTaken(false);
    setImageSrc('');
    startCamera();
  }, [startCamera]);

  const createAbsensi = useCallback(async () => {
    try {
      if (submittedRef.current) {
        return;
      }

      if (!imageSrc) {
        Swal.fire({
          icon: 'warning',
          title: 'Peringatan',
          text: 'Silakan ambil foto terlebih dahulu',
        });
        return;
      }

      if (!latitude || !longitude) {
        Swal.fire({
          icon: 'warning',
          title: 'Peringatan',
          text: 'Silakan tunggu proses lokasi',
        });
        return;
      }

      setIsLoading(true);
      setIsSubmitting(true);
      submittedRef.current = true;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      try {
        const response = await axios.post(
          `${getApiBaseUrl()}/absensi/karyawan/keluar`,
          {
            latitude,
            longitude,
            image: imageSrc,
          },
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json'
            },
            signal: controller.signal,
            timeout: 30000
          }
        );

        clearTimeout(timeoutId);

        Swal.fire({
          icon: 'success',
          title: 'Berhasil',
          text: response.data.msg,
        });

        navigate('/dashboardkaryawan');
      } catch (error) {
        clearTimeout(timeoutId);
        submittedRef.current = false;
        throw error;
      }
    } catch (error) {
      console.error('Error creating absensi:', error);
      
      if (error.name === 'AbortError' || error.code === 'ECONNABORTED') {
        Swal.fire({
          icon: 'error',
          title: 'Timeout',
          text: 'Proses absensi terlalu lama. Silakan coba lagi.',
        });
      } else if (error.response) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response.data?.msg || error.response.data?.message || 'Terjadi kesalahan',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Terjadi kesalahan tidak terduga',
        });
      }
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  }, [imageSrc, latitude, longitude, navigate]);

  const formatCoordinate = (coord) => {
    if (coord === null) return "Menunggu...";
    return coord.toFixed(6);
  };

  const getResponsiveHeight = () => ({
    height: 'calc(100vh - 32px)', 
    maxHeight: '640px', 
    minHeight: '500px'  
  });

  return (
    <Container 
      maxWidth="sm" 
      sx={{ 
        mt: 2, 
        mb: 2,
        width: { xs: '100%', sm: '480px' }, 
        px: 0 
      }}
      disableGutters 
    >
      <Card sx={{ 
        borderRadius: 2, 
        overflow: 'hidden', 
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        bgcolor: '#121212',
        width: '100%'
      }}>
        <Box sx={{ 
          position: 'relative', 
          width: '100%',
          ...getResponsiveHeight(),
          bgcolor: '#000',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden'
        }}>
          {/* Header dengan info lokasi dan profil */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 20,
            p: 1.5,
            bgcolor: 'rgba(0,0,0,0.5)',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <IoBusinessOutline size={16} />
                <span>{namaCabang || 'Loading...'}</span>
              </Typography>
              
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IoTimeOutline size={16} />
                <span>{moment().format('DD MMM YYYY, HH:mm:ss')}</span>
              </Typography>
            </Box>
            {profileImage ? (
              <Avatar src={profileImage} alt="Profile" sx={{ width: 40, height: 40, border: '2px solid white' }} />
            ) : (
              <Avatar sx={{ width: 40, height: 40, bgcolor: 'grey.700', border: '2px solid white' }} />
            )}
          </Box>

          {/* Map & Coordinates overlay (kiri atas) */}
          <Box sx={{ 
            position: 'absolute', 
            left: 12, 
            top: 80, 
            zIndex: 20,
            width: 220,
            bgcolor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(10px)',
            p: 1, 
            borderRadius: 1,
            border: '1px solid rgba(255,255,255,0.2)'
          }}>
            {latitude !== null && longitude !== null ? (
              <Box>
                <Box sx={{ width: '100%', height: 120, overflow: 'hidden', borderRadius: 1, mb: 1 }}>
                  <Lokasi latitude={latitude} longitude={longitude} />
                </Box>
                <Typography variant="caption" color="grey.300" sx={{ display: 'block' }}>
                  Lat: {formatCoordinate(latitude)}
                </Typography>
                <Typography variant="caption" color="grey.300" sx={{ display: 'block' }}>
                  Lng: {formatCoordinate(longitude)}
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="grey.300">Menunggu lokasi...</Typography>
            )}
          </Box>

          {/* Video atau Foto */}
          {!photoTaken ? (
            <>
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  zIndex: 1
                }}
              />

              {/* Face Guide Circle - SELALU TERLIHAT */}
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '240px',
                height: '240px',
                border: '3px solid rgba(255,255,255,0.6)',
                borderRadius: '50%',
                zIndex: 15,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <IoCamera size={48} color="#FFFFFF" style={{ opacity: 0.8 }} />
              </Box>
            </>
          ) : (
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              <img
                src={imageSrc}
                alt="Captured"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </Box>
          )}

          {/* Bottom Action Bar - SELALU TERLIHAT */}
          <Box sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 20,
            bgcolor: 'rgba(0,0,0,0.75)',
            backdropFilter: 'blur(10px)',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5
          }}>
            {/* Koordinat Info */}
            <Box sx={{
              display: 'flex',
              gap: 3,
              color: 'white',
              fontSize: '12px',
              width: '100%',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IoLocationOutline size={14} />
                <span style={{ color: '#999' }}>Lat:</span>
                <span style={{ fontWeight: '600' }}>{formatCoordinate(latitude)}</span>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <IoLocationOutline size={14} />
                <span style={{ color: '#999' }}>Lng:</span>
                <span style={{ fontWeight: '600' }}>{formatCoordinate(longitude)}</span>
              </Box>
            </Box>

            {/* Action Buttons */}
            {!photoTaken ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Button
                  onClick={takePicture}
                  disabled={isLoading}
                  variant="contained"
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    bgcolor: '#2196F3',
                    border: '4px solid rgba(255,255,255,0.3)',
                    minWidth: 0,
                    '&:hover': {
                      bgcolor: '#1976D2',
                      transform: 'scale(1.05)'
                    },
                    boxShadow: '0 4px 12px rgba(33,150,243,0.4)'
                  }}
                >
                  <IoCamera size={32} />
                </Button>
                <Typography variant="body2" color="white" sx={{ textAlign: 'center', maxWidth: '280px', fontSize: '13px' }}>
                  Arahkan wajah dan tekan tombol kamera
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Button
                  onClick={createAbsensi}
                  disabled={isLoading || isSubmitting || !latitude || !longitude}
                  variant="contained"
                  color="success"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(76,175,80,0.3)'
                  }}
                >
                  Kirim
                </Button>
                <Button
                  onClick={retakePicture}
                  disabled={isLoading}
                  variant="outlined"
                  startIcon={<IoRefreshOutline />}
                  sx={{
                    px: 3,
                    py: 1.5,
                    borderRadius: 2,
                    color: 'white',
                    borderColor: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                      borderColor: 'white'
                    }
                  }}
                >
                  Ulangi
                </Button>
              </Box>
            )}
          </Box>

          {/* Loading Bar */}
          {isLoading && (
            <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, zIndex: 25 }} />
          )}

          {/* Loading Overlay */}
          {isSubmitting && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0,0,0,0.7)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 30
            }}>
              <CircularProgress 
                size={60} 
                sx={{ color: '#4CAF50', mb: 2 }} 
              />
              <Typography variant="h6" color="white">
                Mengirim Data...
              </Typography>
              <Typography variant="body2" color="grey.400" sx={{ mt: 1 }}>
                Mohon tunggu sebentar
              </Typography>
            </Box>
          )}

          {/* Back Button */}
          <Box sx={{
            position: 'absolute',
            bottom: 110,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 19
          }}>
            <Button 
              component={NavLink} 
              to="/dashboardkaryawan" 
              variant="text" 
              size="small"
              sx={{ 
                color: 'white', 
                opacity: 0.7, 
                '&:hover': { opacity: 1 } 
              }}
              disabled={isLoading}
            >
              Kembali ke Dashboard
            </Button>
          </Box>
        </Box>
      </Card>
    </Container>
  );
};

export default ClockOut;